/** Sandboxed email preview with a Desktop/Mobile device toggle.
 *
 * Compiled NDD emails are responsive: below the 480px breakpoint every
 * `.ndd-stack-column` stacks (the mobile rendering). Media queries inside an
 * iframe evaluate against the IFRAME's width, and the review panel is
 * ~460px wide — an unscaled preview therefore always showed the MOBILE
 * layout next to the desktop Figma render, which read as a parsing bug.
 * Desktop mode renders at the email's design width (600) and scales the
 * iframe down to fit the panel; Mobile mode renders at a phone width so the
 * stacked layout stays inspectable as the intentional mobile rendering.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { RenderedBodyView } from '@/features/notifications/test-render/RenderedBodyView';

const DESKTOP_WIDTH = 600;
const MOBILE_WIDTH = 390;

interface Props {
  body: string;
  title: string;
  /** Visible preview height in px (the scaled iframe fills it exactly). */
  height?: number;
}

export function ScaledEmailPreview({ body, title, height = 720 }: Props) {
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(DESKTOP_WIDTH);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setContainerWidth(el.clientWidth || DESKTOP_WIDTH);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const base = mode === 'desktop' ? DESKTOP_WIDTH : MOBILE_WIDTH;
  const scale = Math.min(1, containerWidth / base);

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_event, value: 'desktop' | 'mobile' | null) => {
          if (value) setMode(value);
        }}
        sx={{ mb: 0.5 }}
      >
        <ToggleButton value="desktop" sx={{ px: 1, py: 0.25, fontSize: 12 }}>
          Desktop
        </ToggleButton>
        <ToggleButton value="mobile" sx={{ px: 1, py: 0.25, fontSize: 12 }}>
          Mobile
        </ToggleButton>
      </ToggleButtonGroup>
      <Box ref={containerRef} sx={{ height, overflow: 'hidden' }}>
        <Box sx={{ width: base, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <RenderedBodyView body={body} preview title={title} height={Math.round(height / scale)} />
        </Box>
      </Box>
    </Box>
  );
}
